import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import {
  AuditLog,
  AuditLogType,
  AuditLogTypeLabels,
} from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogMedium } from "@/lib/dialogSize";

const ViewExportFilesAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const getLabel = (eventType: AuditLogType): string => {
    switch (eventType) {
      case AuditLogType.CaseFilesExport:
        return "Case:";
      case AuditLogType.FirmDocumentsExport:
        return "Firm Documents Folder:";
      case AuditLogType.ProviderFilesExport:
        return "Provider:";
      case AuditLogType.TemplateFilesExport:
        return "Template:";
      case AuditLogType.IntakeFilesExport:
        return "Intake:";
      default:
        return "";
    }
  };

  const generateDynamicLink = (entityId: string | null, type: AuditLogType) => {
    if (entityId === null) return `/organization/${organizationId}/audit-logs`;

    switch (type) {
      case AuditLogType.CaseFilesExport:
        return `/organization/${organizationId}/case/${entityId}`;
      case AuditLogType.FirmDocumentsExport:
        return `/organization/${organizationId}/firm-documents-folder/${entityId}`;
      case AuditLogType.ProviderFilesExport:
        return `/organization/${organizationId}/provider/${entityId}`;
      case AuditLogType.TemplateFilesExport:
        return `/organization/${organizationId}/template/${entityId}`;
      case AuditLogType.IntakeFilesExport:
        return `/organization/${organizationId}/intake/${entityId}`;
      default:
        return `/organization/${organizationId}/audit-logs`;
    }
  };

  const getEntityId = (
    eventType: AuditLogType,
    additionalData: string | null
  ): string | null => {
    if (!auditLog) return null;

    if (!additionalData) return null;

    const additionalDataAsJson = JSON.parse(additionalData);
    switch (eventType) {
      case AuditLogType.CaseFilesExport:
        return additionalDataAsJson.CaseId;
      case AuditLogType.FirmDocumentsExport:
        return additionalDataAsJson.FirmDocumentsFolderId;
      case AuditLogType.ProviderFilesExport:
        return additionalDataAsJson.ProviderId;
      case AuditLogType.TemplateFilesExport:
        return additionalDataAsJson.TemplateId;
      case AuditLogType.IntakeFilesExport:
        return additionalDataAsJson.IntakeId;
      default:
        return null;
    }
  };

  const parseReason = (additionalData: string) => {
    try {
      const parsedData = JSON.parse(additionalData);
      if (parsedData && typeof parsedData === 'object' && parsedData.Reason) {
        return (
          <span
            className="text-sm whitespace-pre-wrap break-words overflow-y-auto max-h-[15vh] pr-2"
          >
            {parsedData.Reason}
          </span>
        );
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogMedium} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="m-0">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-bold">
                  Reason:
                </span>
                {auditLog.additionalData ? (
                  parseReason(auditLog.additionalData)
                ) : (
                  <></>
                )}
              </div>
            </div>

            <Separator />

            <div className="m-0">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-bold">
                  {getLabel(auditLog.eventType)}
                </span>
                {auditLog.additionalData ? (
                  (() => {
                    try {
                      const entityId = getEntityId(
                        auditLog.eventType,
                        auditLog.additionalData
                      );

                      if (entityId) {
                        return (
                          <span className="text-sm">
                            <a
                              href={generateDynamicLink(
                                entityId,
                                auditLog.eventType
                              )}
                              className="hover:underline text-primary"
                            >
                              {entityId}
                            </a>
                          </span>
                        );
                      }

                      return (
                        <span className="text-sm">
                          Invalid data format
                        </span>
                      );
                    } catch (error) {
                      return (
                        <span className="text-sm">
                          Failed to parse additionalData
                        </span>
                      );
                    }
                  })()
                ) : (
                  <></>
                )}
              </div>
            </div>

            <Separator />

            <LabelValuePair
              label="Performed By"
              value={auditLog.performerEmailAddress}
            />

            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewExportFilesAuditLogDetailsModal;
